import { useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import format from 'date-fns/format';
import html2canvas from 'html2canvas';
import { PDFDocument, PDFImage, rgb, StandardFonts } from 'pdf-lib';
import colors from '../themes/main-colors';
import { formatLocalDate } from '../util/date-utils';
import { hex2rgb } from '../util/format';
import useWrapWithLoader from './useWrapWithLoading';

export type PrintOpts = {
  containerId: string;
  excludeSelectors?: string[];
  minWidth?: number;
  minHeight?: number;
};

export type DownloadOpts = PrintOpts & {
  filename?: string;
  timestampColor?: string;
  timestampFont?: StandardFonts;
  timestampFontsize?: number;
};

export const usePrintOrDownload = ({
  containerId,
  excludeSelectors = [],
  minWidth,
  minHeight,
  filename = `PGE receipt ${formatLocalDate()}.pdf`,
  timestampColor = colors.noirBlur,
  timestampFont = StandardFonts.Helvetica,
  timestampFontsize = 12,
}: DownloadOpts) => {
  const theme = useTheme();
  const windowWidth = minWidth ?? theme.breakpoints.values['md'];
  const [loading, setLoading] = useState<boolean>(false);

  const handleDownload = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const blob = await captureImage();
    if (blob) {
      setLoading(true);
      const doc = await PDFDocument.create();
      const font = await doc.embedFont(timestampFont);
      const spacing = 10;

      const page = doc.addPage();
      const bounds = page.getSize();

      const timestampHeight = font.heightAtSize(timestampFontsize);
      page.moveTo(spacing, bounds.height - timestampHeight - spacing);

      const { r, g, b } = hex2rgb(timestampColor);
      page.drawText(format(new Date(), 'Pp'), {
        size: timestampFontsize,
        font,
        color: rgb(r / 255, g / 255, b / 255),
      });

      //reset bounds height to account for timestamp + spacing
      bounds.height = bounds.height - timestampHeight - spacing * 2;

      const pngImage = await doc.embedPng(await blob.arrayBuffer());

      const { width, height } = pngImage.scaleToFit(
        bounds.width,
        bounds.height,
      );

      page.moveTo(bounds.width / 2 - width / 2, bounds.height - height);

      page.drawImage(pngImage, {
        width,
        height,
      });

      const link = document.createElement('a');
      link.download = filename;
      const href = await doc.saveAsBase64({ dataUri: true });
      link.href = href.replace('application/pdf', 'application/octet-stream');
      link.click();
      setLoading(false);
    }
  };

  const handlePrint = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const blob = await captureImage();
    if (blob) {
      setLoading(true);

      const iframe = document.createElement('iframe');
      iframe.style.visibility = 'hidden';
      iframe.style.width = `${minWidth}px`;

      const img = document.createElement('img');
      img.src = URL.createObjectURL(blob);
      img.onload = () => {
        document.body.appendChild(iframe);
        try {
          minHeight
            ? iframe.contentDocument?.write(
                '<html><head><style>body{margin-top:0;margin-bottom-0;padding-top:0;padding-bottom:0;height: 100%;text-align:center;}</style></head><body><img style="width: auto; height:100%" src="' +
                  img.src +
                  '"/></body></html>',
              )
            : iframe.contentDocument?.write(
                '<html><head><style>body{margin-top:0;margin-bottom-0;padding-top:0;padding-bottom:0;height: 100%;}</style></head><body><img style="width: 100%; height:auto" src="' +
                  img.src +
                  '"/></body></html>',
              );
          const contentWindow = iframe.contentWindow;
          if (contentWindow) {
            contentWindow.onafterprint = () => {
              document.body.removeChild(iframe);
            };
            contentWindow.focus();
            contentWindow.print();
          }
          setLoading(false);
        } catch {
          setLoading(false);
        }
      };
    }
  };

  const captureImage = async (): Promise<Blob | null> => {
    const container = document.getElementById(containerId);
    if (container) {
      const exclude = [
        ...excludeSelectors,
        '[data-print-exclude]',
      ].flatMap(sel => Array.from(document.querySelectorAll(sel).values()));

      exclude.forEach(child =>
        child.setAttribute('data-html2canvas-ignore', 'true'),
      );

      const canvas = await html2canvas(container, {
        onclone: (_: Document, element: HTMLElement) => {
          element
            .querySelectorAll(':scope *')
            .forEach(
              (e: Element) => ((e as HTMLElement).style.boxShadow = 'unset'),
            );
        },
        windowWidth,
      });

      exclude.forEach(child =>
        child.removeAttribute('data-html2canvas-ignore'),
      );

      return new Promise(resolve => canvas.toBlob(resolve));
    }
    return null;
  };

  const handleDownloadWithImage = async (
    e: React.MouseEvent,
    imageArr?: { photo: string; title: string; description: string }[],
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    const blob = await captureImage();
    if (blob) {
      const doc = await PDFDocument.create();
      const font = await doc.embedFont(timestampFont);
      const spacing = 10;

      const page = doc.addPage();
      const bounds = page.getSize();

      const timestampHeight = font.heightAtSize(timestampFontsize);
      page.moveTo(spacing, bounds.height - timestampHeight - spacing);

      const { r, g, b } = hex2rgb(timestampColor);
      page.drawText(format(new Date(), 'Pp'), {
        size: timestampFontsize,
        font,
        color: rgb(r / 255, g / 255, b / 255),
      });

      //reset bounds height to account for timestamp + spacing
      bounds.height = bounds.height - timestampHeight - spacing * 2;

      const pngImage = await doc.embedPng(await blob.arrayBuffer());

      const { width, height } = pngImage.scaleToFit(
        bounds.width,
        bounds.height,
      );

      page.moveTo(bounds.width / 2 - width / 2, bounds.height - height);

      page.drawImage(pngImage, {
        width,
        height,
      });

      const imagesPromises =
        imageArr &&
        imageArr?.map(async item => {
          const response = await fetch(item.photo);
          const photoBlob = await response.blob();

          let pngImageItem: PDFImage;
          if (
            photoBlob.type !== 'image/png' &&
            photoBlob.type !== 'image/jpeg'
          ) {
            console.error('Image is not in valid format:', photoBlob.type);
          } else {
            const photoArrayBuffer = await photoBlob.arrayBuffer();
            if (photoBlob.type === 'image/png') {
              pngImageItem = await doc.embedPng(photoArrayBuffer);
            } else if (photoBlob.type === 'image/jpeg') {
              pngImageItem = await doc.embedJpg(photoArrayBuffer);
            }

            // Ensure 'width' and 'height' are defined and appropriate for drawing images
            const imageWidth = 300; // Replace with actual width
            const imageHeight = 300; // Replace with actual height
            const image_page = doc.addPage();

            image_page.drawText(item.title, {
              x: 150,
              y: 800,
              size: timestampFontsize,
              font,
              color: rgb(0, 0, 0), // Replace with text color
            });

            image_page.drawImage(pngImageItem!, {
              x: 150, // Adjust x position as needed
              y: 400, // Adjust y position for spacing between images
              width: imageWidth,
              height: imageHeight,
            });
          }
        });

      await Promise.all(imagesPromises!);

      const link = document.createElement('a');
      link.download = filename;
      const href = await doc.saveAsBase64({ dataUri: true });
      link.href = href.replace('application/pdf', 'application/octet-stream');
      link.click();

      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  return {
    loading,
    handlePrint,
    handleDownload,
    handleDownloadWithImage,
  };
};
